import { Assert } from "../../Assert";
import { DataSource } from "../../Data/DataSource";
import { PartialNullable } from "../../Extensions";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { IJNativeEvent, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property } from "../../RadObject";
import { ApplicationInformationService } from "../../RadWindow";
import { ServiceManager } from "../../ServiceManager";
import { CheckBoxList } from "./CheckBoxList";
import { ComboBox } from "./ComboBox";
import { ControlRef } from "./ControlRef";
import { Date } from "./Date";
import { DateTime } from "./DateTime";
import { GroupBox } from "./GroupBox";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";
import { Label } from "./Label";
import { Panel } from "./Panel";
import { ScheduleStatic } from "./ScheduleStatic";
import { Time } from "./Time";
import { NAMESPACE } from "./_Namespace";

import "./Schedule.scss";

export type SchedulePropertyValues =
    InputControlPropertyValues
    & {
    };

export type ScheduleProperties = {
    [name in keyof SchedulePropertyValues]: Property<SchedulePropertyValues[name]>
};

export interface ISchedule extends IInputControl, ScheduleProperties
{
}

export class Schedule extends InputControl implements ISchedule
{
    public static override classNamespace = NAMESPACE + "Schedule";
    public static override className = "Schedule";

    // #region Global Variables

    protected dailyFrequencyContainer1?: Panel;
    protected dailyFrequencyContainer2?: Panel;
    protected dailyFrequencyContainer3?: Panel;
    protected dailyFrequencyEveryEndTime?: Time;
    protected dailyFrequencyEveryStartTime?: Time;
    protected dailyFrequencyEveryTimePeriod?: ComboBox;
    protected dailyFrequencyEveryX?: ComboBox;
    protected dailyFrequencyGroupBox?: GroupBox;
    protected dailyFrequencyType?: ComboBox;
    protected dailyFrequencyOnceTime?: Time;
    protected dailyGroupBox?: GroupBox;
    protected durationContainer1?: Panel;
    protected durationContainer2?: Panel;
    protected durationContainer3?: Panel;
    protected durationEndDate?: Date;
    protected durationEndType?: ComboBox;
    protected durationGroupBox?: GroupBox;
    protected durationStartDate?: Date;
    protected frequency?: ComboBox;
    protected frequencyDailyEveryX?: ComboBox;
    protected frequencyMonthlyDayByDayDayNumber?: ComboBox;
    protected frequencyMonthlyDayByDayDayType?: ComboBox;
    protected frequencyMonthlyDayByDayEveryX?: ComboBox;
    protected frequencyMonthlyDayByNumberDayX?: ComboBox;
    protected frequencyMonthlyDayByNumberEveryX?: ComboBox;
    protected frequencyMonthlyFrequencyType?: ComboBox;
    protected frequencyWeeklyEveryX?: ComboBox;
    protected frequencyWeeklyOn?: CheckBoxList;
    protected labelEachDay?: Label;
    protected labelEachDayEvery?: Label;
    protected monthlyContainer1?: Panel;
    protected monthlyContainer2?: Panel;
    protected monthlyContainer3?: Panel;
    protected monthlyGroupBox?: GroupBox;
    protected oneTimeDateTime?: DateTime;
    protected oneTimeGroupBox?: GroupBox;
    protected scheduleTypeGroupBox?: GroupBox;
    protected timeZoneIdGroupBox?: GroupBox;
    protected timeZoneId?: ComboBox;
    protected weeklyContainer1?: Panel;
    protected weeklyContainer2?: Panel;
    protected weeklyGroupBox?: GroupBox;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<SchedulePropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-schedule");
        this.updateLayout();
    }

    // #endregion

    // #region Public Methods

    public updateLayout()
    {
        switch (this.frequency?.value.get())
        {
            case "oneTime":
                this.oneTimeGroupBox?.visible.set(true);
                break;
            default:
                this.oneTimeGroupBox?.visible.set(false);
                break;
        }

        switch (this.frequency?.value.get())
        {
            case "daily":
                this.dailyGroupBox?.visible.set(true);
                break;
            default:
                this.dailyGroupBox?.visible.set(false);
                break;
        }

        switch (this.frequency?.value.get())
        {
            case "weekly":
                this.weeklyGroupBox?.visible.set(true);
                break;
            default:
                this.weeklyGroupBox?.visible.set(false);
                break;
        }

        switch (this.frequency?.value.get())
        {
            case "monthly":
                this.monthlyGroupBox?.visible.set(true);

                switch (this.frequencyMonthlyFrequencyType?.value.get())
                {
                    case "dayByNumber":
                        this.monthlyContainer2?.visible.set(true);
                        break;
                    default:
                        this.monthlyContainer2?.visible.set(false);
                        break;
                }

                switch (this.frequencyMonthlyFrequencyType?.value.get())
                {
                    case "dayByDay":
                        this.monthlyContainer3?.visible.set(true);
                        break;
                    default:
                        this.monthlyContainer3?.visible.set(false);
                        break;
                }

                break;
            default:
                this.monthlyGroupBox?.visible.set(false);
                break;
        }

        switch (this.frequency?.value.get())
        {
            case "daily":
            case "weekly":
            case "monthly":
                this.dailyFrequencyGroupBox?.visible.set(true);

                switch (this.dailyFrequencyType?.value.get())
                {
                    case "once":
                        this.labelEachDay?.visible.set(true);
                        this.dailyFrequencyOnceTime?.visible.set(true);
                        break;
                    default:
                        this.labelEachDay?.visible.set(false);
                        this.dailyFrequencyOnceTime?.visible.set(false);
                        break;
                }

                switch (this.dailyFrequencyType?.value.get())
                {
                    case "recurring":
                        this.labelEachDayEvery?.visible.set(true);
                        this.dailyFrequencyEveryX?.visible.set(true);
                        this.dailyFrequencyEveryTimePeriod?.visible.set(true);
                        this.dailyFrequencyContainer2?.visible.set(true);
                        this.dailyFrequencyContainer3?.visible.set(true);
                        break;
                    default:
                        this.labelEachDayEvery?.visible.set(false);
                        this.dailyFrequencyEveryX?.visible.set(false);
                        this.dailyFrequencyEveryTimePeriod?.visible.set(false);
                        this.dailyFrequencyContainer2?.visible.set(false);
                        this.dailyFrequencyContainer3?.visible.set(false);
                        break;
                }

                this.durationGroupBox?.visible.set(true);

                switch (this.durationEndType?.value.get())
                {
                    case "finite":
                        this.durationContainer3?.visible.set(true);
                        break;
                    default:
                        this.durationContainer3?.visible.set(false);
                        break;
                }

                break;
            default:
                this.dailyFrequencyGroupBox?.visible.set(false);
                this.durationGroupBox?.visible.set(false);
                break;
        }
    }

    // #endregion

    // #region Protected Methods

    protected _elementValueChanged()
    {
        if (!this.__processElementValueChanged)
            return;

        this.value.set(this._elementValueGet());
    }
    protected _elementValueGet()
    {
        const
            frequencyWeeklyOn = this.frequencyWeeklyOn?.value.get() as number[] | undefined;

        return ObjectHelper.toJson({
            timeZoneId: this.timeZoneId?.value.get(),
            frequency: this.frequency?.value.get(),
            oneTimeDateTime: this.oneTimeDateTime?.value.get(),
            frequencyDailyEveryX: this.frequencyDailyEveryX?.value.get(),
            frequencyWeeklyEveryX: this.frequencyWeeklyEveryX?.value.get(),
            frequencyWeeklyOnMonday: frequencyWeeklyOn && frequencyWeeklyOn.indexOf(0) > -1,
            frequencyWeeklyOnTuesday: frequencyWeeklyOn && frequencyWeeklyOn.indexOf(1) > -1,
            frequencyWeeklyOnWednesday: frequencyWeeklyOn && frequencyWeeklyOn.indexOf(2) > -1,
            frequencyWeeklyOnThursday: frequencyWeeklyOn && frequencyWeeklyOn.indexOf(3) > -1,
            frequencyWeeklyOnFriday: frequencyWeeklyOn && frequencyWeeklyOn.indexOf(4) > -1,
            frequencyWeeklyOnSaturday: frequencyWeeklyOn && frequencyWeeklyOn.indexOf(5) > -1,
            frequencyWeeklyOnSunday: frequencyWeeklyOn && frequencyWeeklyOn.indexOf(6) > -1,
            frequencyMonthlyFrequencyType: this.frequencyMonthlyFrequencyType?.value.get(),
            frequencyMonthlyDayByNumberDayX: this.frequencyMonthlyDayByNumberDayX?.value.get(),
            frequencyMonthlyDayByNumberEveryX: this.frequencyMonthlyDayByNumberEveryX?.value.get(),
            frequencyMonthlyDayByDayDayNumber: this.frequencyMonthlyDayByDayDayNumber?.value.get(),
            frequencyMonthlyDayByDayDayType: this.frequencyMonthlyDayByDayDayType?.value.get(),
            frequencyMonthlyDayByDayEveryX: this.frequencyMonthlyDayByDayEveryX?.value.get(),
            dailyFrequencyType: this.dailyFrequencyType?.value.get(),
            dailyFrequencyOnceTime: this.dailyFrequencyOnceTime?.value.get(),
            dailyFrequencyEveryX: this.dailyFrequencyEveryX?.value.get(),
            dailyFrequencyEveryTimePeriod: this.dailyFrequencyEveryTimePeriod?.value.get(),
            dailyFrequencyEveryStartTime: this.dailyFrequencyEveryStartTime?.value.get(),
            dailyFrequencyEveryEndTime: this.dailyFrequencyEveryEndTime?.value.get(),
            durationEndType: this.durationEndType?.value.get(),
            durationStartDate: this.durationStartDate?.value.get(),
            durationEndDate: this.durationEndDate?.value.get()
        });
    }
    protected _elementValueSet(value: any)
    {
        if (!value)
            return;

        this.__processElementValueChanged = false;

        const
            data = ObjectHelper.fromJson(value),
            frequencyWeeklyOn = [];

        if (data.frequencyWeeklyOnMonday)
            frequencyWeeklyOn.push(0);

        if (data.frequencyWeeklyOnTuesday)
            frequencyWeeklyOn.push(1);

        if (data.frequencyWeeklyOnWednesday)
            frequencyWeeklyOn.push(2);

        if (data.frequencyWeeklyOnThursday)
            frequencyWeeklyOn.push(3);

        if (data.frequencyWeeklyOnFriday)
            frequencyWeeklyOn.push(4);

        if (data.frequencyWeeklyOnSaturday)
            frequencyWeeklyOn.push(5);

        if (data.frequencyWeeklyOnSunday)
            frequencyWeeklyOn.push(6);

        this.timeZoneId?.value.set(data.timeZoneId);
        this.frequency?.value.set(data.frequency);
        this.oneTimeDateTime?.value.set(data.oneTimeDateTime);
        this.frequencyDailyEveryX?.value.set(data.frequencyDailyEveryX);
        this.frequencyWeeklyEveryX?.value.set(data.frequencyWeeklyEveryX);
        this.frequencyWeeklyOn?.value.set(frequencyWeeklyOn);
        this.frequencyMonthlyFrequencyType?.value.set(data.frequencyMonthlyFrequencyType);
        this.frequencyMonthlyDayByNumberDayX?.value.set(data.frequencyMonthlyDayByNumberDayX);
        this.frequencyMonthlyDayByNumberEveryX?.value.set(data.frequencyMonthlyDayByNumberEveryX);
        this.frequencyMonthlyDayByDayDayNumber?.value.set(data.frequencyMonthlyDayByDayDayNumber);
        this.frequencyMonthlyDayByDayDayType?.value.set(data.frequencyMonthlyDayByDayDayType);
        this.frequencyMonthlyDayByDayEveryX?.value.set(data.frequencyMonthlyDayByDayEveryX);
        this.dailyFrequencyType?.value.set(data.dailyFrequencyType);
        this.dailyFrequencyOnceTime?.value.set(data.dailyFrequencyOnceTime);
        this.dailyFrequencyEveryX?.value.set(data.dailyFrequencyEveryX);
        this.dailyFrequencyEveryTimePeriod?.value.set(data.dailyFrequencyEveryTimePeriod);
        this.dailyFrequencyEveryStartTime?.value.set(data.dailyFrequencyEveryStartTime);
        this.dailyFrequencyEveryEndTime?.value.set(data.dailyFrequencyEveryEndTime);
        this.durationEndType?.value.set(data.durationEndType);
        this.durationStartDate?.value.set(data.durationStartDate);
        this.durationEndDate?.value.set(data.durationEndDate);

        this.__processElementValueChanged = true;
    }
    protected _validateValue(value: string)
    {
        const
            valueObject = value ? ObjectHelper.fromJson(value) : null;

        if (!valueObject)
            return false;

        switch (valueObject.frequency)
        {
            case "oneTime":
                if (valueObject.oneTimeDateTime === null)
                    return false;
                break;
            case "daily":
                if (valueObject.frequencyDailyEveryX === null)
                    return false;
                break;
            case "weekly":
                if
                    (
                    valueObject.frequencyWeeklyEveryX === null
                    ||
                    valueObject.frequencyWeeklyOnMonday === null
                    && valueObject.frequencyWeeklyOnTuesday === null
                    && valueObject.frequencyWeeklyOnWednesday === null
                    && valueObject.frequencyWeeklyOnThursday === null
                    && valueObject.frequencyWeeklyOnFriday === null
                    && valueObject.frequencyWeeklyOnSaturday === null
                    && valueObject.frequencyWeeklyOnSunday === null
                )
                    return false;
                break;
            case "monthly":
                switch (valueObject.frequencyMonthlyFrequencyType)
                {
                    case "dayByNumber":
                        if
                            (
                            valueObject.frequencyMonthlyDayByNumberEveryX === null
                            || valueObject.frequencyMonthlyDayByNumberDayX === null
                        )
                            return false;
                        break;
                    case "dayByDay":
                        if
                            (
                            valueObject.frequencyMonthlyDayByDayDayNumber === null
                            || valueObject.frequencyMonthlyDayByDayDayType === null
                            || valueObject.frequencyMonthlyDayByDayEveryX === null
                        )
                            return false;
                        break;
                    default:
                        return false;
                }
                break;
            default:
                return false;
        }

        switch (valueObject.frequency)
        {
            case "oneTime":
                break;
            default:
                switch (valueObject.dailyFrequencyType)
                {
                    case "once":
                        if (valueObject.dailyFrequencyOnceTime === null)
                            return false;
                        break;
                    case "recurring":
                        if
                            (
                            valueObject.dailyFrequencyEveryX === null
                            || valueObject.dailyFrequencyEveryTimePeriod === null
                            || valueObject.dailyFrequencyEveryStartTime === null
                            || valueObject.dailyFrequencyEveryEndTime === null
                        )
                            return false;
                        break;
                    default:
                        return false;
                }
                break;
        }

        switch (valueObject.frequency)
        {
            case "oneTime":
                break;
            default:
                switch (valueObject.durationEndType)
                {
                    case "none":
                        if (valueObject.durationStartDate === null)
                            return false;
                        break;
                    case "finite":
                        if
                            (
                            valueObject.durationStartDate === null
                            || valueObject.durationEndDate === null
                        )
                            return false;
                        break;
                    default:
                        return false;
                }
                break;
        }

        return true;
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: SchedulePropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.text.overrideGet(next =>
        {
            const
                value = this.value.get();

            return value ? ScheduleStatic.getDescription(ObjectHelper.fromJson(value)) : StringHelper.empty;
        });
        this.value.overrideSet((value, next) =>
        {
            this._elementValueSet(value);
            next(this._validateValue(value) ? value : null);
        });
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        Assert.isNotNullOrUndefined(this.timeZoneId, "this.timeZoneId");
        Assert.isNotNullOrUndefined(this.frequency, "this.frequency");
        Assert.isNotNullOrUndefined(this.oneTimeDateTime, "this.oneTimeDateTime");
        Assert.isNotNullOrUndefined(this.frequencyDailyEveryX, "this.frequencyDailyEveryX");
        Assert.isNotNullOrUndefined(this.frequencyWeeklyEveryX, "this.frequencyWeeklyEveryX");
        Assert.isNotNullOrUndefined(this.frequencyWeeklyOn, "this.frequencyWeeklyOn");
        Assert.isNotNullOrUndefined(this.frequencyMonthlyFrequencyType, "this.frequencyMonthlyFrequencyType");
        Assert.isNotNullOrUndefined(this.frequencyMonthlyDayByNumberDayX, "this.frequencyMonthlyDayByNumberDayX");
        Assert.isNotNullOrUndefined(this.frequencyMonthlyDayByNumberEveryX, "this.frequencyMonthlyDayByNumberEveryX");
        Assert.isNotNullOrUndefined(this.frequencyMonthlyDayByDayDayNumber, "this.frequencyMonthlyDayByDayDayNumber");
        Assert.isNotNullOrUndefined(this.frequencyMonthlyDayByDayDayType, "this.frequencyMonthlyDayByDayDayType");
        Assert.isNotNullOrUndefined(this.frequencyMonthlyDayByDayEveryX, "this.frequencyMonthlyDayByDayEveryX");
        Assert.isNotNullOrUndefined(this.dailyFrequencyType, "this.dailyFrequencyType");
        Assert.isNotNullOrUndefined(this.dailyFrequencyOnceTime, "this.dailyFrequencyOnceTime");
        Assert.isNotNullOrUndefined(this.dailyFrequencyEveryX, "this.dailyFrequencyEveryX");
        Assert.isNotNullOrUndefined(this.dailyFrequencyEveryTimePeriod, "this.dailyFrequencyEveryTimePeriod");
        Assert.isNotNullOrUndefined(this.dailyFrequencyEveryStartTime, "this.dailyFrequencyEveryStartTime");
        Assert.isNotNullOrUndefined(this.dailyFrequencyEveryEndTime, "this.dailyFrequencyEveryEndTime");
        Assert.isNotNullOrUndefined(this.durationStartDate, "this.durationStartDate");
        Assert.isNotNullOrUndefined(this.durationEndType, "this.durationEndType");
        Assert.isNotNullOrUndefined(this.durationEndDate, "this.durationEndDate");

        this.timeZoneId.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.frequency.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.oneTimeDateTime.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.frequencyDailyEveryX.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.frequencyWeeklyEveryX.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.frequencyWeeklyOn.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.frequencyMonthlyFrequencyType.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.frequencyMonthlyDayByNumberDayX.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.frequencyMonthlyDayByNumberEveryX.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.frequencyMonthlyDayByDayDayNumber.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.frequencyMonthlyDayByDayDayType.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.frequencyMonthlyDayByDayEveryX.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.dailyFrequencyType.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.dailyFrequencyOnceTime.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.dailyFrequencyEveryX.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.dailyFrequencyEveryTimePeriod.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.dailyFrequencyEveryStartTime.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.dailyFrequencyEveryEndTime.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.durationStartDate.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.durationEndType.on("valueChanged", { self: this }, this.onControlValueChanged);
        this.durationEndDate.on("valueChanged", { self: this }, this.onControlValueChanged);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement(),
            labelWidth = 100,
            applicationInformation = ServiceManager.getRequired(ApplicationInformationService);

        this.timeZoneIdGroupBox = new GroupBox({ text: "Time Zone" });
        $$element.append(this.timeZoneIdGroupBox.$$element.get());

        this.timeZoneId = new ComboBox({
            text: "Time Zone",
            listDataSource: new DataSource({ data: applicationInformation.timeZones.map(item => ({ value: item.id, description: item.description })) }),
        });
        this.timeZoneIdGroupBox.append(this.timeZoneId);

        this.scheduleTypeGroupBox = new GroupBox({ text: "Schedule Type" });
        $$element?.append(this.scheduleTypeGroupBox.$$element.get());

        this.frequency = new ComboBox({
            text: "Frequency",
            inline: true,
            minWidth: 112,
            listDataSource: new DataSource({
                data: [
                    { value: "oneTime", description: "One Time" },
                    { value: "daily", description: "Daily" },
                    { value: "weekly", description: "Weekly" },
                    { value: "monthly", description: "Monthly" }
                ]
            })
        });
        this.scheduleTypeGroupBox.append(new Label({ inline: true, text: "Frequency", minWidth: labelWidth, associatedControlRef: new ControlRef(this.frequency) }));
        this.scheduleTypeGroupBox.append(this.frequency);

        // One Time
        this.oneTimeGroupBox = new GroupBox({ text: "One Time Occurance" });
        $$element?.append(this.oneTimeGroupBox.$$element.get());

        this.oneTimeDateTime = new DateTime({ inline: true });
        this.oneTimeGroupBox.append(new Label({ inline: true, text: "Date", minWidth: labelWidth, associatedControlRef: new ControlRef(this.oneTimeDateTime) }));
        this.oneTimeGroupBox.append(this.oneTimeDateTime);

        // Daily
        this.dailyGroupBox = new GroupBox({ text: "Daily Occurance" });
        $$element?.append(this.dailyGroupBox.$$element.get());

        this.frequencyDailyEveryX = new ComboBox({
            inline: true,
            minWidth: 70,
            listDataSource: new DataSource({
                data: function ()
                {
                    const
                        options = [];

                    for (let i = 1; i <= 100; i++)
                        options.push({ value: i, description: i.toString() });

                    return options;
                }()
            })
        });
        this.dailyGroupBox.append(new Label({ inline: true, text: "Recurs every", minWidth: labelWidth, associatedControlRef: new ControlRef(this.frequencyDailyEveryX) }));
        this.dailyGroupBox.append(this.frequencyDailyEveryX);
        this.dailyGroupBox.append(new Label({ inline: true, colon: false, text: "day(s)" }));

        // Weekly
        this.weeklyGroupBox = new GroupBox({ text: "Weekly Occurance" });
        $$element?.append(this.weeklyGroupBox.$$element.get());

        this.weeklyContainer1 = new Panel();
        this.weeklyGroupBox.append(this.weeklyContainer1);

        this.frequencyWeeklyEveryX = new ComboBox({
            inline: true,
            minWidth: 70,
            listDataSource: new DataSource({
                data: function ()
                {
                    const
                        options = [];

                    for (let i = 1; i <= 100; i++)
                        options.push({ value: i, description: i.toString() });

                    return options;
                }()
            })
        });
        this.weeklyContainer1.append(new Label({ inline: true, text: "Recurs every", minWidth: labelWidth, associatedControlRef: new ControlRef(this.frequencyWeeklyEveryX) }));
        this.weeklyContainer1.append(this.frequencyWeeklyEveryX);
        this.weeklyContainer1.append(new Label({ inline: true, colon: false, text: "week(s)" }));

        this.weeklyContainer2 = new Panel();
        this.weeklyGroupBox.append(this.weeklyContainer2);

        this.frequencyWeeklyOn = new CheckBoxList({
            inline: true,
            listDataSource: new DataSource({
                data: [
                    { value: 0, description: "Monday" },
                    { value: 1, description: "Tuesday" },
                    { value: 2, description: "Wednesday" },
                    { value: 3, description: "Thursday" },
                    { value: 4, description: "Friday" },
                    { value: 5, description: "Saturday" },
                    { value: 6, description: "Sunday" }
                ]
            })
        });
        this.weeklyContainer2.append(new Label({ inline: true, text: "On", minWidth: labelWidth, associatedControlRef: new ControlRef(this.frequencyWeeklyOn), cssClass: "rad-vertical-align-top" }));
        this.weeklyContainer2.append(this.frequencyWeeklyOn);

        // Monthly
        this.monthlyGroupBox = new GroupBox({ text: "Monthly Occurance" });
        $$element?.append(this.monthlyGroupBox.$$element.get());

        this.monthlyContainer1 = new Panel();
        this.monthlyGroupBox.append(this.monthlyContainer1);

        this.frequencyMonthlyFrequencyType = new ComboBox({
            inline: true,
            minWidth: 126,
            listDataSource: new DataSource({
                data: [
                    { value: "dayByNumber", description: "By Number" },
                    { value: "dayByDay", description: "By Type" }
                ]
            })
        });
        this.monthlyContainer1.append(new Label({ inline: true, text: "Type", minWidth: labelWidth, associatedControlRef: new ControlRef(this.frequencyMonthlyFrequencyType) }));
        this.monthlyContainer1.append(this.frequencyMonthlyFrequencyType);

        this.monthlyContainer2 = new Panel();
        this.monthlyGroupBox.append(this.monthlyContainer2);

        this.frequencyMonthlyDayByNumberDayX = new ComboBox({
            inline: true,
            minWidth: 63,
            listDataSource: new DataSource({
                data: function ()
                {
                    const
                        options = [];

                    for (let i = 1; i <= 31; i++)
                        options.push({ value: i, description: i.toString() });

                    return options;
                }()
            })
        });
        this.monthlyContainer2.append(new Label({ inline: true, text: "On day", minWidth: labelWidth, associatedControlRef: new ControlRef(this.frequencyMonthlyDayByNumberDayX) }));
        this.monthlyContainer2.append(this.frequencyMonthlyDayByNumberDayX);

        this.frequencyMonthlyDayByNumberEveryX = new ComboBox({
            inline: true,
            minWidth: 70,
            listDataSource: new DataSource({
                data: function ()
                {
                    const
                        options = [];

                    for (let i = 1; i <= 100; i++)
                        options.push({ value: i, description: i.toString() });

                    return options;
                }()
            })
        });
        this.monthlyContainer2.append(new Label({ inline: true, text: "of every", associatedControlRef: new ControlRef(this.frequencyMonthlyDayByNumberEveryX) }));
        this.monthlyContainer2.append(this.frequencyMonthlyDayByNumberEveryX);
        this.monthlyContainer2.append(new Label({ inline: true, colon: false, text: "month(s)" }));

        this.monthlyContainer3 = new Panel();
        this.monthlyGroupBox.append(this.monthlyContainer3);

        this.frequencyMonthlyDayByDayDayNumber = new ComboBox({
            inline: true,
            minWidth: 91,
            listDataSource: new DataSource({
                data: [
                    { value: "first", description: "First" },
                    { value: "second", description: "Second" },
                    { value: "third", description: "Third" },
                    { value: "fourth", description: "Fourth" },
                    { value: "last", description: "Last" }
                ]
            })
        });
        this.monthlyContainer3.append(new Label({ inline: true, text: "On the", minWidth: labelWidth, associatedControlRef: new ControlRef(this.frequencyMonthlyDayByDayDayNumber) }));
        this.monthlyContainer3.append(this.frequencyMonthlyDayByDayDayNumber);

        this.frequencyMonthlyDayByDayDayType = new ComboBox({
            inline: true,
            minWidth: 140,
            listDataSource: new DataSource({
                data: [
                    { value: "monday", description: "Monday" },
                    { value: "tuesday", description: "Tuesday" },
                    { value: "wednesday", description: "Wednesday" },
                    { value: "thursday", description: "Thursday" },
                    { value: "friday", description: "Friday" },
                    { value: "saturday", description: "Saturday" },
                    { value: "sunday", description: "Sunday" },
                    { value: "day", description: "Day" },
                    { value: "weekDay", description: "Week Day" },
                    { value: "weekendDay", description: "Weekend Day" }
                ]
            })
        });
        this.monthlyContainer3.append(this.frequencyMonthlyDayByDayDayType);

        this.frequencyMonthlyDayByDayEveryX = new ComboBox({
            inline: true,
            minWidth: 70,
            listDataSource: new DataSource({
                data: function ()
                {
                    const
                        options = [];

                    for (let i = 1; i <= 100; i++)
                        options.push({ value: i, description: i.toString() });

                    return options;
                }()
            })
        });
        this.monthlyContainer3.append(new Label({ inline: true, text: "of every", associatedControlRef: new ControlRef(this.frequencyMonthlyDayByDayEveryX) }));
        this.monthlyContainer3.append(this.frequencyMonthlyDayByDayEveryX);
        this.monthlyContainer3.append(new Label({ inline: true, colon: false, text: "month(s)" }));

        // Daily Frequency
        this.dailyFrequencyGroupBox = new GroupBox({ text: "Daily Frequency" });
        $$element?.append(this.dailyFrequencyGroupBox.$$element.get());

        this.dailyFrequencyContainer1 = new Panel();
        this.dailyFrequencyGroupBox.append(this.dailyFrequencyContainer1);

        this.dailyFrequencyType = new ComboBox({
            inline: true,
            minWidth: 119,
            listDataSource: new DataSource({
                data: [
                    { value: "once", description: "Once" },
                    { value: "recurring", description: "Repeatedly" }
                ]
            })
        });
        this.dailyFrequencyContainer1.append(new Label({ inline: true, text: "Occurs", minWidth: labelWidth, associatedControlRef: new ControlRef(this.dailyFrequencyType) }));
        this.dailyFrequencyContainer1.append(this.dailyFrequencyType);
        this.labelEachDay = new Label({ inline: true, text: "each day, at" });
        this.dailyFrequencyContainer1.append(this.labelEachDay);
        this.labelEachDayEvery = new Label({ inline: true, text: "each day, every" });
        this.dailyFrequencyContainer1.append(this.labelEachDayEvery);

        this.dailyFrequencyOnceTime = new Time({ inline: true });
        this.dailyFrequencyContainer1.append(this.dailyFrequencyOnceTime);
        this.labelEachDay.associatedControlRef.set(new ControlRef(this.dailyFrequencyOnceTime));

        this.dailyFrequencyEveryX = new ComboBox({
            inline: true,
            minWidth: 63,
            listDataSource: new DataSource({
                data: function ()
                {
                    const
                        options = [];

                    for (let i = 1; i <= 60; i++)
                        options.push({ value: i, description: i.toString() });

                    return options;
                }()
            })
        });
        this.dailyFrequencyContainer1.append(this.dailyFrequencyEveryX);
        this.labelEachDayEvery.associatedControlRef.set(new ControlRef(this.dailyFrequencyEveryX));

        this.dailyFrequencyEveryTimePeriod = new ComboBox({
            inline: true,
            minWidth: 112,
            listDataSource: new DataSource({
                data: [
                    { value: "hours", description: "Hour(s)" },
                    { value: "minutes", description: "Minute(s)" },
                    { value: "seconds", description: "Second(s)" }
                ]
            })
        });
        this.dailyFrequencyContainer1.append(this.dailyFrequencyEveryTimePeriod);

        this.dailyFrequencyContainer2 = new Panel();
        this.dailyFrequencyGroupBox.append(this.dailyFrequencyContainer2);

        this.dailyFrequencyEveryStartTime = new Time({ inline: true });
        this.dailyFrequencyContainer2.append(new Label({ inline: true, text: "Between", minWidth: labelWidth, associatedControlRef: new ControlRef(this.dailyFrequencyEveryStartTime) }));
        this.dailyFrequencyContainer2.append(this.dailyFrequencyEveryStartTime);

        this.dailyFrequencyContainer3 = new Panel();
        this.dailyFrequencyGroupBox.append(this.dailyFrequencyContainer3);

        this.dailyFrequencyEveryEndTime = new Time({ inline: true });
        this.dailyFrequencyContainer3.append(new Label({ inline: true, text: "And", minWidth: labelWidth, associatedControlRef: new ControlRef(this.dailyFrequencyEveryEndTime) }));
        this.dailyFrequencyContainer3.append(this.dailyFrequencyEveryEndTime);

        // Duration
        this.durationGroupBox = new GroupBox({ text: "Duration" });
        $$element?.append(this.durationGroupBox.$$element.get());

        this.durationContainer1 = new Panel();
        this.durationGroupBox.append(this.durationContainer1);

        this.durationStartDate = new Date({ inline: true });
        this.durationContainer1.append(new Label({ inline: true, text: "From", minWidth: labelWidth, associatedControlRef: new ControlRef(this.durationStartDate) }));
        this.durationContainer1.append(this.durationStartDate);

        this.durationContainer2 = new Panel();
        this.durationGroupBox.append(this.durationContainer2);

        this.durationEndType = new ComboBox({
            inline: true,
            minWidth: 91,
            listDataSource: new DataSource({
                data: [
                    { value: "none", description: "None" },
                    { value: "finite", description: "Finite" }
                ]
            })
        });
        this.durationContainer2.append(new Label({ inline: true, text: "End Type", minWidth: labelWidth, associatedControlRef: new ControlRef(this.durationEndType) }));
        this.durationContainer2.append(this.durationEndType);

        this.durationContainer3 = new Panel();
        this.durationGroupBox.append(this.durationContainer3);

        this.durationEndDate = new Date({ inline: true });
        this.durationContainer3.append(new Label({ inline: true, text: "To", minWidth: labelWidth, associatedControlRef: new ControlRef(this.durationEndDate) }));
        this.durationContainer3.append(this.durationEndDate);

        return $$element;
    }
    protected override _enabledChanged(change: PropertyChange<boolean>)
    {
        super._enabledChanged(change);

        this.$$element.get()?.find(".rad-control").each((index, element) =>
        {
            const
                control = JNativeStatic.radControl(element);

            if (control)
                control.enabled.set(change.currentValue);
        });
    }

    public override remove()
    {
        if (this.frequency)
        {
            this.frequency.remove();
            delete this.frequency;
        }

        if (this.oneTimeDateTime)
        {
            this.oneTimeDateTime.remove();
            delete this.oneTimeDateTime;
        }

        if (this.frequencyDailyEveryX)
        {
            this.frequencyDailyEveryX.remove();
            delete this.frequencyDailyEveryX;
        }

        if (this.frequencyWeeklyEveryX)
        {
            this.frequencyWeeklyEveryX.remove();
            delete this.frequencyWeeklyEveryX;
        }

        if (this.frequencyWeeklyOn)
        {
            this.frequencyWeeklyOn.remove();
            delete this.frequencyWeeklyOn;
        }

        if (this.frequencyMonthlyFrequencyType)
        {
            this.frequencyMonthlyFrequencyType.remove();
            delete this.frequencyMonthlyFrequencyType;
        }

        if (this.frequencyMonthlyDayByNumberDayX)
        {
            this.frequencyMonthlyDayByNumberDayX.remove();
            delete this.frequencyMonthlyDayByNumberDayX;
        }

        if (this.frequencyMonthlyDayByNumberEveryX)
        {
            this.frequencyMonthlyDayByNumberEveryX.remove();
            delete this.frequencyMonthlyDayByNumberEveryX;
        }

        if (this.frequencyMonthlyDayByDayDayNumber)
        {
            this.frequencyMonthlyDayByDayDayNumber.remove();
            delete this.frequencyMonthlyDayByDayDayNumber;
        }

        if (this.frequencyMonthlyDayByDayDayType)
        {
            this.frequencyMonthlyDayByDayDayType.remove();
            delete this.frequencyMonthlyDayByDayDayType;
        }

        if (this.frequencyMonthlyDayByDayEveryX)
        {
            this.frequencyMonthlyDayByDayEveryX.remove();
            delete this.frequencyMonthlyDayByDayEveryX;
        }

        if (this.dailyFrequencyType)
        {
            this.dailyFrequencyType.remove();
            delete this.dailyFrequencyType;
        }

        if (this.dailyFrequencyOnceTime)
        {
            this.dailyFrequencyOnceTime.remove();
            delete this.dailyFrequencyOnceTime;
        }

        if (this.dailyFrequencyEveryX)
        {
            this.dailyFrequencyEveryX.remove();
            delete this.dailyFrequencyEveryX;
        }

        if (this.dailyFrequencyEveryTimePeriod)
        {
            this.dailyFrequencyEveryTimePeriod.remove();
            delete this.dailyFrequencyEveryTimePeriod;
        }

        if (this.dailyFrequencyEveryStartTime)
        {
            this.dailyFrequencyEveryStartTime.remove();
            delete this.dailyFrequencyEveryStartTime;
        }

        if (this.dailyFrequencyEveryEndTime)
        {
            this.dailyFrequencyEveryEndTime.remove();
            delete this.dailyFrequencyEveryEndTime;
        }

        if (this.durationStartDate)
        {
            this.durationStartDate.remove();
            delete this.durationStartDate;
        }

        if (this.durationEndType)
        {
            this.durationEndType.remove();
            delete this.durationEndType;
        }

        if (this.durationEndDate)
        {
            this.durationEndDate.remove();
            delete this.durationEndDate;
        }

        super.remove();
    }

    // #endregion

    // #region Event Handlers

    protected onControlValueChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._elementValueChanged();
        self.updateLayout();
    }

    // #endregion
}

